interface RangeSelectionArgs<T> {
  models: T[];
  singleModel: T;
  selectedItems: T[];
  index: number;
  checked: boolean;
  totalRecs: number;
  isShiftPressed: boolean;
  shiftSelectStart: number | null;
  shiftSelectEnd: number | null;
}

interface RangeSelectionResult<T> {
  tempShiftSelectStart: number | null;
  tempShiftSelectEnd: number | null;
  tempSelectedItems: T[];
  allSelected: boolean;
}

/**
 * Shift-range selection: clicking a checkbox with Shift held selects every row
 * between the previous click and the current one.
 * Reference: https://github.com/customerio/ui/pull/4611
 */
export function rangeSelectionHelper<T>(args: RangeSelectionArgs<T>): RangeSelectionResult<T> {
  const { index, checked, totalRecs, isShiftPressed, shiftSelectStart, shiftSelectEnd } = args;
  let tempShiftSelectStart = shiftSelectStart;
  let tempShiftSelectEnd: number | null;
  let allSelected: boolean;
  let tempSelectedItems = [...args.selectedItems];

  if (isShiftPressed && shiftSelectStart != null && shiftSelectEnd != null) {
    const removeStart = Math.min(shiftSelectStart, shiftSelectEnd);
    const removeEnd = Math.max(shiftSelectStart, shiftSelectEnd);

    const itemsToRemove = args.models.slice(removeStart, removeEnd + 1);
    tempSelectedItems = tempSelectedItems.filter((item) => !itemsToRemove.includes(item));

    tempShiftSelectEnd = index;

    const start = Math.min(index, shiftSelectStart);
    const end = Math.max(index, shiftSelectStart);

    const itemsToSelect = args.models.slice(start, end + 1);
    tempSelectedItems = [...new Set([...tempSelectedItems, ...itemsToSelect])];

    allSelected = tempSelectedItems.length === totalRecs;
  } else {
    if (checked) {
      if (!tempSelectedItems.includes(args.singleModel)) {
        tempSelectedItems = [...tempSelectedItems, args.singleModel];
      }
      allSelected = tempSelectedItems.length === totalRecs;
    } else {
      allSelected = false;
      tempSelectedItems = tempSelectedItems.filter((item) => item !== args.singleModel);
    }

    tempShiftSelectStart = index;
    tempShiftSelectEnd = index;
  }

  return {
    tempShiftSelectStart,
    tempShiftSelectEnd,
    tempSelectedItems,
    allSelected,
  };
}
